import './index.scss'
import ApplicationController from '@app/javascript/controllers/application_controller'
import I18n from '@app/javascript/i18n/index.js.erb'
import { useDispatch } from 'stimulus-use'

export class Controller extends ApplicationController {
  static targets = [
    'name',
    'selectMultipleComponent',
    'form',
    'submitButton',
    'clearableController',
    'logicalOperatorMessage'
  ]

  static disabledAttributeName = `data-${this.identifier}-disabled`

  doConnect() {
    useDispatch(this)

    if (!this.isActive()) {
      this.clearButton?.setAttribute('disabled', true)
    }
  }

  setActive() {
    this.element.classList.add('c-filter-active')
    this.clearButton?.removeAttribute('disabled')
  }

  setInactive() {
    this.element.classList.remove('c-filter-active')
    this.clearButton?.setAttribute('disabled', true)
  }

  isActive() {
    return this.element.classList.contains('c-filter-active')
  }

  get filterName() {
    return this.element.getAttribute('data-filter-name')
  }

  get childInputs() {
    return this.element.querySelectorAll('input')
  }

  get childPills() {
    return this.element.querySelectorAll('.molecule-pill-removable')
  }

  get clearButton() {
    return this.element.querySelector('.c-filter__header-clear')
  }

  disableClearAll() {
    this.element.setAttribute(this.disabledAttributeName, true)
  }

  enableClearAll() {
    this.element.removeAttribute(this.disabledAttributeName)
  }

  isClearEnabled() {
    return !this.element.hasAttribute(this.disabledAttributeName)
  }

  resetStateAndCollapse() {
    // Clears any selected value from the filter without triggering a reload event
    this.clearAll(false)
    this.collapse()
  }

  collapse() {
    this.element.controllers['class-toggler'].toggleAllBackToOriginal()
  }

  trigger(_ev) {
    this.refreshActiveStatus()
    this.dispatch('filterTriggered')
  }

  clearAll(reload = true) {
    if (this.isClearEnabled()) {
      this.childInputs.forEach((input) => this.clearInput(input))

      this.selectMultipleComponentTargets.forEach(element => {
        element.controllers['select-multiple-component'].clearAll()
      })

      this.childPills.forEach((pill) => this.clearPill(pill))

      this.clearableControllerTargets.forEach(target => {
        const event = new CustomEvent(`${this.identifier}:clearInputs`, { bubbles: false })
        target.dispatchEvent(event)
      })

      this.setInactive()

      if (reload) this.dispatch('filterCleared')
    }
  }

  clearValues(values, reload = true) {
    if (!this.isClearEnabled()) return

    values.forEach((value) => {
      this.clearValue(value, false)
    })

    this.refreshActiveStatus()

    if (reload) this.dispatch('filterCleared')
  }

  clearValue(value, reload = true) {
    if (this.isClearEnabled()) {
      this.childInputs.forEach((input) => {
        if (value === input.value) this.clearInput(input)
      })

      this.selectMultipleComponentTargets.forEach(element => {
        element.controllers['select-multiple-component'].clearValue(value)
      })

      this.childPills.forEach((pill) => {
        if (value === pill.getAttribute('data-pill-value')) this.clearPill(pill)
      })

      if (reload) this.dispatch('filterCleared')
    }
  }

  clearInput(inputElem) {
    const inputType = inputElem.type.toLowerCase()

    switch (inputType) {
      case 'text':
      case 'password':
      case 'textarea':
      case 'search':
      case 'hidden':
      case 'date':
        inputElem.value = ''
        break
      case 'radio':
        inputElem.checked = inputElem.dataset.default || inputElem.hasAttribute('checked')
        break
      case 'checkbox':
        inputElem.checked = false
        inputElem.removeAttribute('checked')
        break
      case 'select-one':
      case 'select-multi':
        inputElem.selectedIndex = -1
        break
      default:
        break
    }

    // NOTE: Before, the event 'clear' was being fired. But this caused some
    // glitches in the Search page (for some unknown reason). That's why we changed
    // the name from 'clear' to 'cleared'.
    inputElem.dispatchEvent(new CustomEvent('cleared'))
  }

  clearPill(pillElem) {
    pillElem.parentNode.removeChild(pillElem)
  }

  refreshActiveStatus() {
    const isActive = Array.from(this.childInputs).find((input) => !this.isInputCleared(input))

    if (isActive) {
      this.setActive()
    } else {
      this.setInactive()
    }
  }

  isInputCleared(inputElem) {
    const inputType = inputElem.type.toLowerCase()

    switch (inputType) {
      case 'text':
      case 'password':
      case 'textarea':
      case 'search':
      case 'hidden':
      case 'date':
        return !inputElem.value
      case 'checkbox':
      case 'radio':
        return !inputElem.checked || inputElem.hasAttribute('data-default-checked')
      case 'select-one':
      case 'select-multi':
        return !Object.hasOwnProperty.call(inputElem, 'selectedIndex') || inputElem.selectedIndex === -1
      default:
        return true
    }
  }

  /**
   * Toggle the filter's data-test-id value, based on its current state (expanded/collapsed)
   */
  toggleFilterContainerTestId() {
    const testId = this.element.dataset.testId
    const stateSuffixIndex = testId.lastIndexOf('_')
    let testIdNew = testId.slice(0, stateSuffixIndex + 1)
    testIdNew += testId.indexOf('expanded') === -1 ? 'expanded' : 'collapsed'
    this.element.dataset.testId = testIdNew
  }

  /**
   * Show the user a clarifying message about the logical operator they've selected
   */
  updateLogicalOperatorMessage(event) {
    if (this.hasLogicalOperatorMessageTarget) {
      const input = event.target
      const operator = input.getAttribute('data-operator-id') || input.value.toLowerCase()

      this.logicalOperatorMessageTarget.innerHTML = I18n.t(`template.labels.filter_${operator}_msg.html`)
    }
  }
}
